import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EmployeeService } from '@shared/services/mesuser.service';
import { IScannerData, ScannerListenerService } from '@shared/services/scanner-listener.service';
import { IScanningPrefix, ScanningPrefixService } from '@shared/services/scanning-prefix.service';
import { IWorktask } from '@shared/services/worktask.service';
import { catchError, EMPTY, Subject, switchMap, take, takeUntil } from 'rxjs';

@Component({
	selector: 'app-employee-checkin',
	templateUrl: './employee-checkin.component.html',
	styleUrls: ['./employee-checkin.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class EmployeeCheckinComponent implements AfterViewInit, OnDestroy {
	public errorMessage = '';
	public showErrorMessage = false;
	public inputData: string;
	public worktask: IWorktask;
	private linkPrefixes: IScanningPrefix[] = [];
	private readonly destroy$ = new Subject<void>();

	// A new scan/click pushes here, cancelling whatever employee lookup + active-worktask fetch
	// was still in flight for the previous employee - without this, an earlier response arriving
	// after a later scan could overwrite the current employee's worktask with stale data.
	private readonly setCurrentEmployee$ = new Subject<string>();

	@ViewChild('employeeNumberInputField')
	employeeNumberInputField: ElementRef<HTMLInputElement>;

	constructor(
		public employeeService: EmployeeService,
		private translator: TranslateService,
		prefixService: ScanningPrefixService,
		scannerListenerService: ScannerListenerService,
	) {
		prefixService
			.getAll()
			.pipe(take(1))
			.subscribe((e) => {
				this.linkPrefixes = e;
			});

		scannerListenerService
			.ScannerDataChanged()
			.pipe(takeUntil(this.destroy$))
			.subscribe((c) => this.onScan(c));

		this.setCurrentEmployee$
			.pipe(
				switchMap((inputData) =>
					this.employeeService.getEmployeeBySalaryKey(inputData).pipe(
						switchMap((response) => {
							if (!response.result.isActive) {
								setTimeout(() => (this.showErrorMessage = false), 3000);
								this.errorMessage = this.translator.instant('mes.mes-production-terminal.errors.alias-not-active', { salaryCode: inputData });
								this.showErrorMessage = true;
								return EMPTY;
							}

							this.employeeService.currentEmployee = response.result;
							return this.employeeService.getActiveWorkTask(response.result.id);
						}),
						catchError((errorResponse: HttpErrorResponse) => {
							this.errorMessage = this.handleError(errorResponse, inputData);
							this.showErrorMessage = true;
							return EMPTY;
						}),
					),
				),
				takeUntil(this.destroy$),
			)
			.subscribe((workTaskResponse) => {
				this.worktask = workTaskResponse.result;
			});
	}

	ngAfterViewInit(): void {
		this.employeeNumberInputField.nativeElement.focus();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	onScan(scannerData: IScannerData) {
		const isLinkScan = this.linkPrefixes.some((x) => scannerData.prefix === x.prefix || (scannerData.token ?? '').startsWith(x.prefix));

		if (!isLinkScan) {
			if (scannerData.prefix === '' && !this.employeeService.currentEmployee) {
				// The listener hears keystrokes wherever focus is, but inputData only fills while the field
				// has focus - fall back to what the listener caught, unless the keys went into a text field.
				const salaryKey = this.inputData || (this.isTextFieldFocused() ? '' : scannerData.token.trim());
				this.setCurrentEmployee(salaryKey);
			}
			if (scannerData.prefix === ScannerListenerService.scannerPrefixes.employeePrefix) {
				this.setCurrentEmployee(scannerData.token);
			}
		}

		this.inputData = '';
	}

	onButtonClick() {
		if (!this.employeeService.currentEmployee) {
			if (this.inputData && this.inputData > '') {
				this.setCurrentEmployee(this.inputData);
			}
		} else {
			this.resetCurrentEmployee();
		}
	}

	private setCurrentEmployee(inputData: string) {
		if (!inputData || inputData === '') return;

		this.setCurrentEmployee$.next(inputData);
	}

	// A focused text field owns the keystrokes: our own field reports through inputData, which also
	// sees Backspace - the listener's copy does not, so an emptied field would resubmit erased digits.
	private isTextFieldFocused(): boolean {
		const active = document.activeElement as HTMLElement | null;
		return !!active && (active.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName));
	}

	private resetCurrentEmployee() {
		this.employeeService.currentEmployee = null;
		this.worktask = null;
		this.inputData = '';
	}

	private handleError(errorResponse: HttpErrorResponse, salaryCode: string): string {
		if (errorResponse.status === 404) {
			setTimeout(() => (this.showErrorMessage = false), 3000);
			return this.translator.instant('mes.mes-production-terminal.errors.alias-not-found', {
				salaryCode,
			});
		} else if (errorResponse.error instanceof ErrorEvent) {
			return errorResponse.error.message;
		} else {
			return `Error Code: ${errorResponse.status}\nMessage: ${errorResponse.message}`;
		}
	}

	getCurrentEmployeeMessage(): string {
		const firstName = this.employeeService.currentEmployee?.firstName ?? '';
		const lastName = this.employeeService.currentEmployee?.lastName ?? '';
		const worktask = this.worktask?.name ?? '';
		return this.employeeService.currentEmployee ? `${firstName} ${lastName} (${worktask})` : '';
	}
}
